import { useContext } from 'react';

import { AIChatInput, CustomQualificationRequestData, Image } from '@vyce/core/src/types';
import { AI_MODEL } from '@vyce/core/src/types/ai';
import { createResponseAiRequest } from '@vyce/core/src/api/ai';
import { useBooleanState } from '@vyce/core/src/hooks';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

import { QualificationCaptureRequests, QualificationFields } from '../types';
import {
  CSCS_PROMPT,
  CSCS_TASK_NAME,
  CUSTOM_SOURCE,
  INDUSTRY_CRT_TYPE,
  QUALIFICATION_PROMPT,
  emptyQualificationFields,
} from '../config';
import { normalizeExpiryDate, parseFields, photosToImageContent } from '../utils';

interface Args extends QualificationCaptureRequests {
  userId: string;
  userLastName?: string;
  reRequestData: () => void;
}

export const useQualificationCapture = ({
  userId,
  userLastName,
  reRequestData,
  createCustomQualificationRequest,
  uploadCustomQualificationImageRequest,
  triggerCheckerRequest,
  saveCSCSRefNumberRequest,
}: Args) => {
  const { handleServerError } = useContext(NotificationContext);
  const [extracting, setExtractingTrue, setExtractingFalse] = useBooleanState(false);
  const [saving, setSavingTrue, setSavingFalse] = useBooleanState(false);

  // Send the captured photos to the AI (via the AWS OpenAI proxy) and parse the
  // structured fields back. Falls back to empty fields so the user can fill manually.
  const extractFromPhotos = async (
    photos: Image[],
    isCSCS: boolean
  ): Promise<QualificationFields> => {
    try {
      setExtractingTrue();
      const imageContent = await photosToImageContent(photos);
      const input: AIChatInput[] = [
        {
          role: 'user',
          content: [
            { type: 'input_text', text: isCSCS ? CSCS_PROMPT : QUALIFICATION_PROMPT },
            ...imageContent,
          ],
        },
      ];
      const res = await createResponseAiRequest({ input, model: AI_MODEL.GPT_4O });
      return parseFields(res.data?.body?.output_text || '');
    } catch (e) {
      console.error('Qualification extraction failed', e);
      return { ...emptyQualificationFields };
    } finally {
      setExtractingFalse();
    }
  };

  // CSCS cards are also verified against the official register. Fire-and-forget —
  // the result lands on the profile asynchronously.
  const triggerCSCSCheck = (referenceNumber: string) => {
    try {
      saveCSCSRefNumberRequest?.({ serialNumber: referenceNumber, userId });
      triggerCheckerRequest({
        task_name: CSCS_TASK_NAME,
        parameters: {
          serial_number: referenceNumber,
          scheme_id: 'CSCS',
          surname: userLastName,
        },
        userId,
      });
    } catch (e) {
      console.error('CSCS verification failed', e);
    }
  };

  // Create the custom qualification, attach the original photos and (for CSCS)
  // kick off the official register check, then refresh the profile list.
  const saveQualification = async (
    fields: QualificationFields,
    photos: Image[],
    isCSCS: boolean
  ): Promise<boolean> => {
    try {
      setSavingTrue();
      const payload: CustomQualificationRequestData = {
        crt_type: INDUSTRY_CRT_TYPE,
        source: CUSTOM_SOURCE,
        title: fields.qualification_name,
        issuer: fields.issuing_body,
        reference_number: fields.reference_number || undefined,
        // A non-existent date (e.g. "2030-02-29") would be rejected by the backend.
        expiry_date: normalizeExpiryDate(fields.expiry_date) || undefined,
      };

      const { data } = await createCustomQualificationRequest({ data: payload, userId });
      const certificateId = data.uuid;

      for (const photo of photos) {
        if (photo.file) {
          await uploadCustomQualificationImageRequest({ file: photo.file, certificateId, userId });
        }
      }

      if (isCSCS && fields.reference_number) {
        triggerCSCSCheck(fields.reference_number);
      }

      reRequestData();
      return true;
    } catch (e) {
      handleServerError(e);
      return false;
    } finally {
      setSavingFalse();
    }
  };

  return { extractFromPhotos, saveQualification, extracting, saving };
};
